<template>
  <div class="bg-white rounded-2xl border border-[#1c4848]/10 shadow-sm flex flex-col min-h-0">
    <div class="p-6 pb-3">
      <h3 class="text-[18px] font-medium tracking-tight text-[#1c4848]">{{ title }}</h3>
      <p class="text-[14px] text-[#1c4848]/55 mt-1">{{ subtitle }}</p>
    </div>

    <p v-if="!rows.length" class="text-base text-[#1c4848]/40 py-10 text-center px-6">Nobody did anything in this period.</p>

    <div v-else class="relative flex-1 min-h-0">
      <ol class="absolute inset-0 overflow-y-auto custom-scrollbar px-6 pb-5">
        <li
          v-for="(row, index) in rows"
          :key="row.user_id"
          class="flex items-center gap-3 py-3"
          :class="index !== rows.length - 1 ? 'border-b border-[#1c4848]/[0.07]' : ''"
        >
          <span class="w-6 text-[13px] text-[#1c4848]/35 tabular-nums shrink-0">{{ index + 1 }}</span>
          <div class="min-w-0 flex-1">
            <button type="button" class="text-[15px] font-medium text-[#1c4848] hover:underline" title="Show only this person in the main log" @click="emit('pick', row.user_id)">
              User #{{ row.user_id }}
            </button>
            <span v-if="row.premium === 'active'" class="ml-2 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-[#ecd79f]/50 text-[#7a5d12]">Premium</span>
            <span v-if="isTest(row)" class="ml-1 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{{ envLabel(row.environment) }}</span>
            <p class="text-[12px] text-[#1c4848]/45 mt-0.5 truncate">{{ deviceLine(row) }}</p>
          </div>
          <div class="text-right shrink-0">
            <p class="text-[15px] font-semibold text-[#1c4848] tabular-nums">{{ row.total }}</p>
            <p class="text-[11px] text-[#1c4848]/40">{{ row.kinds }} kinds · {{ ago(row.last_at) }}</p>
          </div>
        </li>
      </ol>
    </div>
  </div>
</template>

<script setup>
import { regionName } from '@/utils/regionName.js';

defineProps({
  title: { type: String, default: 'Busiest people' },
  subtitle: { type: String, default: '' },
  /** [{ user_id, total, kinds, last_at, environment, device_model, … }] from analytics/top-users. */
  rows: { type: Array, default: () => [] },
});
const emit = defineEmits(['pick']);

const ENVIRONMENTS = { production: 'App Store', testflight: 'TestFlight', debug: 'Xcode', simulator: 'Simulator' };
const envLabel = (env) => ENVIRONMENTS[env] || env;
const isTest = (row) => ['testflight', 'debug', 'simulator'].includes(row.environment);

function deviceLine(row) {
  const parts = [];
  if (row.device_model) parts.push(row.device_model);
  if (row.os_version) parts.push(`iOS ${row.os_version}`);
  if (row.app_version) parts.push(row.build ? `v${row.app_version} (${row.build})` : `v${row.app_version}`);
  if (row.region) parts.push(regionName(row.region));
  if (row.joined_at) parts.push(`joined ${new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(row.joined_at))}`);
  return parts.join(' · ') || 'Older app build';
}

function ago(iso) {
  const minutes = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)} h ago`;
  return `${Math.round(minutes / 1440)} d ago`;
}
</script>
